import React, { useCallback, useEffect, useMemo, useState } from 'react';
import {
  Alert,
  Box,
  Button,
  CircularProgress,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  FormControlLabel,
  IconButton,
  InputAdornment,
  Snackbar,
  Switch,
  Autocomplete,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TablePagination,
  TableRow,
  TableFooter,
  TextField,
  Typography,
  Paper
} from '@mui/material';
import { Add, ArrowDownward, ArrowUpward, Clear, CloudDownload, Delete, Edit, FileDownload, FirstPage, LastPage, NavigateBefore, NavigateNext } from '@mui/icons-material';
import { useAuthSession } from './AuthSessionContext';
import axiosInstance from './axiosInstance';
import { formatDate } from './dateUtils';
import './SuppliersPage.css';
import './SupplierGridView.css';

const defaultFormState = {
  supplier_id: '',
  name: '',
  active: true,
  nominal_code_id: '',
  tax_code_id: '',
  supplier_category_id: '',
  supplier_status_id: 'approved',
  discount: '0',
  discount_start_date: '',
};

const normaliseMessage = (message) => {
  if (!message) {
    return 'An unexpected error occurred.';
  }

  if (typeof message === 'string') {
    return message;
  }

  if (typeof message === 'object') {
    if (message.message) {
      return message.message;
    }
    if (message.detail) {
      return message.detail;
    }
    if (message.error) {
      return message.error;
    }
    if (Array.isArray(message)) {
      return message.filter(Boolean).join(', ');
    }
    try {
      return JSON.stringify(message);
    } catch (_error) {
      return 'An unexpected error occurred.';
    }
  }

  return String(message);
};

const normaliseDateInputValue = (value) => {
  if (!value) return '';
  const text = String(value);
  if (text.includes('T')) {
    return text.split('T')[0];
  }
  return text;
};

const normaliseSupplierKey = (supplier) => {
  if (!supplier) return '';
  const { supplier_id: rawId } = supplier;
  if (rawId === null || rawId === undefined) return '';
  return String(rawId).trim().toLowerCase();
};

const formatDiscountDisplay = (value) => {
  if (value === null || value === undefined || value === '') return '-';
  const parsed = Number(value);
  if (Number.isNaN(parsed)) return '-';
  return `${parsed.toFixed(2)}%`;
};

const PaginationActions = ({ page, count, rowsPerPage, onPageChange, disabled }) => {
  const handleFirstPage = (event) => {
    onPageChange(event, 0);
  };

  const handleBackPage = (event) => {
    onPageChange(event, page - 1);
  };

  const handleNextPage = (event) => {
    onPageChange(event, page + 1);
  };

  const handleLastPage = (event) => {
    const lastPage = Math.max(0, Math.ceil(count / rowsPerPage) - 1);
    onPageChange(event, lastPage);
  };

  return (
    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
      <IconButton
        onClick={handleFirstPage}
        disabled={disabled || page === 0}
        aria-label="first page"
        size="small"
      >
        <FirstPage fontSize="small" />
      </IconButton>
      <IconButton
        onClick={handleBackPage}
        disabled={disabled || page === 0}
        aria-label="previous page"
        size="small"
      >
        <NavigateBefore fontSize="small" />
      </IconButton>
      <IconButton
        onClick={handleNextPage}
        disabled={disabled || page >= Math.ceil(count / rowsPerPage) - 1}
        aria-label="next page"
        size="small"
      >
        <NavigateNext fontSize="small" />
      </IconButton>
      <IconButton
        onClick={handleLastPage}
        disabled={disabled || page >= Math.ceil(count / rowsPerPage) - 1}
        aria-label="last page"
        size="small"
      >
        <LastPage fontSize="small" />
      </IconButton>
    </Box>
  );
};

const SuppliersPage = () => {
  const { roles: userRoles } = useAuthSession();
  const isAdmin = userRoles.includes('admin');
  const isReaderOnly =
    userRoles.includes('reader') &&
    !userRoles.some((role) => role === 'admin' || role === 'reviewer');

  const [suppliers, setSuppliers] = useState([]);
  const [allSuppliers, setAllSuppliers] = useState([]);
  const [selectedSupplierFilter, setSelectedSupplierFilter] = useState(null);
  const [selectedSupplierCategoryFilter, setSelectedSupplierCategoryFilter] = useState(null);
  const [selectedNominalFilter, setSelectedNominalFilter] = useState(null);
  const [selectedTaxFilter, setSelectedTaxFilter] = useState(null);
  const [selectedSupplierStatusFilter, setSelectedSupplierStatusFilter] = useState(null);
  const [nominalCodes, setNominalCodes] = useState([]);
  const [taxCodes, setTaxCodes] = useState([]);
  const [supplierCategories, setSupplierCategories] = useState([]);
  const [supplierStatuses, setSupplierStatuses] = useState([]);
  const [loading, setLoading] = useState(false);
  const [page, setPage] = useState(0);
  const [pageChanging, setPageChanging] = useState(false);
  const rowsPerPage = 10;
  const [pageInput, setPageInput] = useState('1');
  const [sortKey, setSortKey] = useState(null);
  const [sortDirection, setSortDirection] = useState('asc');
  const [hoveredHeader, setHoveredHeader] = useState(null);

  const [dialogOpen, setDialogOpen] = useState(false);
  const [dialogMode, setDialogMode] = useState('create');
  const [formData, setFormData] = useState(defaultFormState);
  const [formErrors, setFormErrors] = useState({});

  const [deleteTarget, setDeleteTarget] = useState(null);
  const [busyAction, setBusyAction] = useState(false);
  const [exportingSuppliers, setExportingSuppliers] = useState(false);

  const [snackbar, setSnackbar] = useState({ open: false, message: '', severity: 'success' });
  const [hasFetchedSuppliers, setHasFetchedSuppliers] = useState(false);

  const closeSnackbar = () => setSnackbar((prev) => ({ ...prev, open: false }));

  const showMessage = useCallback((message, severity = 'success') => {
    setSnackbar({ open: true, message: normaliseMessage(message), severity });
  }, []);

  const fetchSuppliers = useCallback(async (params = {}) => {
    setLoading(true);
    try {
      const response = await axiosInstance.get('/api/v1/supplier', { params });
      const list = response?.data?.data || [];
      setSuppliers(Array.isArray(list) ? list : []);
    } catch (error) {
      console.error('Failed to fetch suppliers:', error);
      showMessage(
        normaliseMessage(error?.response?.data) || 'Failed to load suppliers. Please try again later.',
        'error'
      );
    } finally {
      setLoading(false);
      setHasFetchedSuppliers(true);
    }
  }, [showMessage]);

  const fetchSupplierOptions = useCallback(async () => {
    try {
      const response = await axiosInstance.get('/api/v1/supplier');
      const list = response?.data?.data || [];
      setAllSuppliers(Array.isArray(list) ? list : []);
    } catch (error) {
      console.error('Failed to fetch supplier list for filters:', error);
    }
  }, []);

  const fetchReferenceData = useCallback(async () => {
    try {
      const [nominalResponse, taxResponse, supplierCategoryResponse, supplierStatusResponse] = await Promise.all([
        axiosInstance.get('/api/v1/nominal-code'),
        axiosInstance.get('/api/v1/tax-code'),
        axiosInstance.get('/api/v1/supplier/category'),
        axiosInstance.get('/api/v1/supplier/status'),
      ]);

      const nominalList = nominalResponse?.data?.data || [];
      const taxList = taxResponse?.data?.data || [];
      const supplierCategoryList = supplierCategoryResponse?.data?.data || [];
      const supplierStatusList = supplierStatusResponse?.data?.data || [];

      setNominalCodes(Array.isArray(nominalList) ? nominalList : []);
      setTaxCodes(Array.isArray(taxList) ? taxList : []);
      setSupplierCategories(Array.isArray(supplierCategoryList) ? supplierCategoryList : []);
      setSupplierStatuses(Array.isArray(supplierStatusList) ? supplierStatusList : []);
    } catch (error) {
      console.error('Failed to fetch reference data:', error);
      showMessage(
        normaliseMessage(error?.response?.data) || 'Failed to load nominal/tax codes. Some values may be missing.',
        'error'
      );
    }
  }, [showMessage]);

  const supplierQueryParams = useMemo(() => {
    const params = {};
    if (selectedSupplierFilter?.supplier_id) {
      params.supplier_id = selectedSupplierFilter.supplier_id;
    }
    if (selectedSupplierCategoryFilter?.supplier_category_id) {
      params.supplier_category_id = selectedSupplierCategoryFilter.supplier_category_id;
    }
    if (selectedNominalFilter?.nominal_code_id) {
      params.nominal_code_id = selectedNominalFilter.nominal_code_id;
    }
    if (selectedTaxFilter?.tax_code_id) {
      params.tax_code_id = selectedTaxFilter.tax_code_id;
    }
    if (selectedSupplierStatusFilter?.supplier_status_id) {
      params.supplier_status_id = selectedSupplierStatusFilter.supplier_status_id;
    }
    return params;
  }, [
    selectedSupplierFilter,
    selectedSupplierCategoryFilter,
    selectedNominalFilter,
    selectedTaxFilter,
    selectedSupplierStatusFilter,
  ]);

  useEffect(() => {
    fetchSuppliers(supplierQueryParams);
  }, [fetchSuppliers, supplierQueryParams]);

  useEffect(() => {
    fetchSupplierOptions();
  }, [fetchSupplierOptions]);

  useEffect(() => {
    fetchReferenceData();
  }, [fetchReferenceData]);

  const supplierFilterOptions = useMemo(() => {
    if (!Array.isArray(allSuppliers)) return [];
    const map = new Map();
    allSuppliers.forEach((supplier) => {
      const key = normaliseSupplierKey(supplier);
      if (!key || map.has(key)) return;
      map.set(key, supplier);
    });
    return Array.from(map.values()).sort((a, b) => {
      const nameA = (a?.name || '').toLowerCase();
      const nameB = (b?.name || '').toLowerCase();
      if (nameA < nameB) return -1;
      if (nameA > nameB) return 1;
      return 0;
    });
  }, [allSuppliers]);

  useEffect(() => {
    if (!selectedSupplierFilter) return;
    const key = normaliseSupplierKey(selectedSupplierFilter);
    const stillAvailable = supplierFilterOptions.some((supplier) => normaliseSupplierKey(supplier) === key);
    if (!stillAvailable) {
      setSelectedSupplierFilter(null);
    }
  }, [supplierFilterOptions, selectedSupplierFilter]);

  const filteredSuppliers = suppliers;

  useEffect(() => {
    setPage(0);
    setPageInput('1');
  }, [supplierQueryParams]);

  const handleSupplierFilterChange = useCallback((_event, newValue) => {
    setSelectedSupplierFilter(newValue || null);
    setPage(0);
    setPageInput('1');
  }, []);

  const handleSupplierCategoryFilterChange = useCallback((_event, newValue) => {
    setSelectedSupplierCategoryFilter(newValue || null);
    setPage(0);
    setPageInput('1');
  }, []);

  const handleNominalFilterChange = useCallback((_event, newValue) => {
    setSelectedNominalFilter(newValue || null);
    setPage(0);
    setPageInput('1');
  }, []);

  const handleTaxFilterChange = useCallback((_event, newValue) => {
    setSelectedTaxFilter(newValue || null);
    setPage(0);
    setPageInput('1');
  }, []);

  const handleSupplierStatusFilterChange = useCallback((_event, newValue) => {
    setSelectedSupplierStatusFilter(newValue || null);
    setPage(0);
    setPageInput('1');
  }, []);

  const hasActiveFilters = useMemo(
    () =>
      Boolean(
        selectedSupplierFilter ||
          selectedSupplierCategoryFilter ||
          selectedNominalFilter ||
          selectedTaxFilter ||
          selectedSupplierStatusFilter
      ),
    [
      selectedSupplierFilter,
      selectedSupplierCategoryFilter,
      selectedNominalFilter,
      selectedTaxFilter,
      selectedSupplierStatusFilter,
    ]
  );

  const handleClearFilters = useCallback(() => {
    setSelectedSupplierFilter(null);
    setSelectedSupplierCategoryFilter(null);
    setSelectedNominalFilter(null);
    setSelectedTaxFilter(null);
    setSelectedSupplierStatusFilter(null);
    setPage(0);
    setPageInput('1');
  }, []);

  const totalPages = filteredSuppliers.length ? Math.ceil(filteredSuppliers.length / rowsPerPage) : 0;
  const startIndex = filteredSuppliers.length ? page * rowsPerPage + 1 : 0;
  const endIndex = filteredSuppliers.length ? Math.min((page + 1) * rowsPerPage, filteredSuppliers.length) : 0;

  useEffect(() => {
    if (!filteredSuppliers.length) {
      setPage(0);
      setPageInput('1');
      return;
    }

    const clampedPage = Math.min(page, Math.max(totalPages - 1, 0));
    if (clampedPage !== page) {
      setPage(clampedPage);
      setPageInput(String(clampedPage + 1));
      return;
    }

    setPageInput(String(page + 1));
  }, [filteredSuppliers.length, page, totalPages]);

  const nominalCodeMap = useMemo(() => {
    return Array.isArray(nominalCodes)
      ? nominalCodes.reduce((acc, code) => {
          if (code?.nominal_code_id) {
            acc[code.nominal_code_id] = code.name || code.nominal_code_id;
          }
          return acc;
        }, {})
      : {};
  }, [nominalCodes]);

  const taxCodeMap = useMemo(() => {
    return Array.isArray(taxCodes)
      ? taxCodes.reduce((acc, code) => {
          if (code?.tax_code_id) {
            acc[code.tax_code_id] = code.name || code.tax_code_id;
          }
          return acc;
        }, {})
      : {};
  }, [taxCodes]);

  const supplierCategoryOptions = useMemo(() => supplierCategories, [supplierCategories]);
  const supplierStatusOptions = useMemo(() => supplierStatuses, [supplierStatuses]);


  const categoryNameMap = useMemo(() => {
    return Array.isArray(supplierCategoryOptions)
      ? supplierCategoryOptions.reduce((acc, category) => {
          if (category?.supplier_category_id) {
            acc[category.supplier_category_id] = category.name || category.supplier_category_id;
          }
          return acc;
        }, {})
      : {};
  }, [supplierCategoryOptions]);

  const statusNameMap = useMemo(() => {
    return Array.isArray(supplierStatusOptions)
      ? supplierStatusOptions.reduce((acc, status) => {
          if (status?.supplier_status_id) {
            acc[status.supplier_status_id] = status.name || status.supplier_status_id;
          }
          return acc;
        }, {})
      : {};
  }, [supplierStatusOptions]);

  const getSortValue = useCallback(
    (supplier, key) => {
      if (!key) return '';
      switch (key) {
        case 'supplier_id':
          return supplier.supplier_id || '';
        case 'name':
          return supplier.name || '';
        case 'supplier_category_id':
          return categoryNameMap[supplier.supplier_category_id] || supplier.supplier_category_id || '';
        case 'nominal_code_id':
          return nominalCodeMap[supplier.nominal_code_id] || supplier.nominal_code_id || '';
        case 'tax_code_id':
          return taxCodeMap[supplier.tax_code_id] || supplier.tax_code_id || '';
        case 'supplier_status_id':
          return statusNameMap[supplier.supplier_status_id] || supplier.supplier_status_id || '';
        case 'created_on':
          return supplier.created_on ? new Date(supplier.created_on).getTime() : 0;
        case 'discount_start_date':
          return supplier.discount_start_date ? new Date(supplier.discount_start_date).getTime() : 0;
        case 'discount':
          return Number(supplier.discount || 0);
        default:
          return supplier[key] ?? '';
      }
    },
    [categoryNameMap, nominalCodeMap, statusNameMap, taxCodeMap]
  );

  const sortedSuppliers = useMemo(() => {
    if (!sortKey) return filteredSuppliers;
    const direction = sortDirection === 'asc' ? 1 : -1;
    return [...filteredSuppliers].sort((a, b) => {
      const valueA = getSortValue(a, sortKey);
      const valueB = getSortValue(b, sortKey);

      if (typeof valueA === 'number' && typeof valueB === 'number') {
        return (valueA - valueB) * direction;
      }
      return String(valueA).localeCompare(String(valueB), undefined, { sensitivity: 'base' }) * direction;
    });
  }, [filteredSuppliers, getSortValue, sortDirection, sortKey]);

  const paginatedSuppliers = useMemo(() => {
    const start = page * rowsPerPage;
    return sortedSuppliers.slice(start, start + rowsPerPage);
  }, [sortedSuppliers, page, rowsPerPage]);

  const hasTableRows = paginatedSuppliers.length > 0;
  const isTableLoading = loading || pageChanging;
  const footerSummary = filteredSuppliers.length
    ? `Showing ${startIndex}-${endIndex} of ${filteredSuppliers.length}`
    : hasFetchedSuppliers
      ? 'No record(s) found.'
      : 'Showing 0-0 of 0';

  const handlePageInputChange = (event) => {
    const numericValue = event.target.value.replace(/[^0-9]/g, '');
    setPageInput(numericValue);
  };

  const handlePageInputKeyDown = (event) => {
    if (event.key !== 'Enter') return;

    event.preventDefault();
    if (!filteredSuppliers.length) {
      setPageInput('1');
      return;
    }

    const desiredPage = Number(pageInput);
    if (!Number.isFinite(desiredPage) || desiredPage < 1) {
      setPageInput(String(page + 1));
      return;
    }

    const clampedPage = Math.min(Math.max(Math.floor(desiredPage), 1), totalPages || 1);
    if (clampedPage - 1 !== page) {
      setPageChanging(true);
      setPage(clampedPage - 1);
    }
  };

  const handleSortClick = (key) => {
    if (!key) return;
    if (sortKey === key) {
      setSortDirection((prev) => (prev === 'asc' ? 'desc' : 'asc'));
    } else {
      setSortKey(key);
      setSortDirection('asc');
    }
    setHoveredHeader(null);
  };

  const handlePageChange = (_event, newPage) => {
    if (newPage === page) return;
    setPageChanging(true);
    setPage(newPage);
  };

  useEffect(() => {
    if (!pageChanging) {
      return undefined;
    }

    const timer = setTimeout(() => {
      setPageChanging(false);
    }, 200);

    return () => clearTimeout(timer);
  }, [pageChanging, page, filteredSuppliers.length]);

  const supplierNominalOptions = useMemo(() => nominalCodes, [nominalCodes]);
  const supplierTaxOptions = useMemo(() => taxCodes, [taxCodes]);

  const validateForm = (values) => {
    const errors = {};
    if (!values.supplier_id?.trim()) {
      errors.supplier_id = 'Supplier ID is required';
    }
    if (!values.name?.trim()) {
      errors.name = 'Supplier name is required';
    }
    return errors;
  };

  const openCreateDialog = () => {
    setDialogMode('create');
    setFormData(defaultFormState);
    setFormErrors({});
    setDialogOpen(true);
  };

  const openEditDialog = (supplier) => {
    setDialogMode('edit');
    setFormData({
      supplier_id: supplier.supplier_id || '',
      name: supplier.name || '',
      active: supplier.active !== undefined ? supplier.active : true,
      nominal_code_id: supplier.nominal_code_id || '',
      tax_code_id: supplier.tax_code_id || '',
      supplier_category_id: supplier.supplier_category_id || '',
      supplier_status_id: supplier.supplier_status_id || 'approved',
      discount: supplier.discount !== undefined && supplier.discount !== null ? String(supplier.discount) : '0',
      discount_start_date: normaliseDateInputValue(supplier.discount_start_date),
    });
    setFormErrors({});
    setDialogOpen(true);
  };

  const closeDialog = () => {
    if (busyAction) return;
    setDialogOpen(false);
    setFormErrors({});
  };

  const handleFormChange = (field, value) => {
    setFormData((prev) => ({ ...prev, [field]: value }));
  };

  const handleSaveSupplier = async () => {
    const errors = validateForm(formData);
    setFormErrors(errors);
    if (Object.keys(errors).length > 0) {
      return;
    }

    setBusyAction(true);
    try {
      const hasDiscountValue = formData.discount !== '' && formData.discount !== null && formData.discount !== undefined;
      const parsedDiscount = hasDiscountValue ? parseFloat(formData.discount) : 0.0;
      const payload = {
        ...formData,
        discount: Number.isNaN(parsedDiscount) ? 0.0 : parsedDiscount,
        discount_start_date: formData.discount_start_date || null,
        nominal_code_id: formData.nominal_code_id || null,
        tax_code_id: formData.tax_code_id || null,
        supplier_category_id: formData.supplier_category_id || null,
        supplier_status_id: formData.supplier_status_id || null,
      };

      if (dialogMode === 'create') {
        await axiosInstance.post('/api/v1/supplier', payload);
        showMessage('Supplier created successfully.');
      } else {
        await axiosInstance.put(`/api/v1/supplier/${formData.supplier_id}`, payload);
        showMessage('Supplier updated successfully.');
      }
      setDialogOpen(false);
      await fetchSuppliers(supplierQueryParams);
      await fetchSupplierOptions();
    } catch (error) {
      console.error('Failed to save supplier:', error);
      const errorMessage =
        normaliseMessage(error?.response?.data?.detail || error?.response?.data) || 'Failed to save supplier. Please try again.';
      showMessage(errorMessage, 'error');
    } finally {
      setBusyAction(false);
    }
  };

  const confirmDeleteSupplier = (supplier) => {
    setDeleteTarget(supplier);
  };

  const cancelDelete = () => {
    setDeleteTarget(null);
  };

  const handleDeleteSupplier = async () => {
    if (!deleteTarget) return;
    setBusyAction(true);
    try {
      await axiosInstance.delete(`/api/v1/supplier/${deleteTarget.supplier_id}`);
      showMessage('Supplier deleted successfully.');
      setDeleteTarget(null);
      await fetchSuppliers(supplierQueryParams);
      await fetchSupplierOptions();
    } catch (error) {
      console.error('Failed to delete supplier:', error);
      const errorMessage =
        normaliseMessage(error?.response?.data?.detail || error?.response?.data) || 'Failed to delete supplier. Please try again.';
      showMessage(errorMessage, 'error');
    } finally {
      setBusyAction(false);
    }
  };

  const handleImportSuppliers = async () => {
    setBusyAction(true);
    try {
      await axiosInstance.post('/api/v1/supplier/import');
      showMessage('Supplier import started.');
      await fetchSuppliers(supplierQueryParams);
      await fetchSupplierOptions();
    } catch (error) {
      console.error('Failed to import suppliers:', error);
      const errorMessage =
        normaliseMessage(error?.response?.data?.detail || error?.response?.data) || 'Failed to import suppliers. Please try again.';
      showMessage(errorMessage, 'error');
    } finally {
      setBusyAction(false);
    }
  };

  const handleExportSuppliersCsv = async () => {
    if (exportingSuppliers) return;
    try {
      setExportingSuppliers(true);
      if (!filteredSuppliers.length) {
        showMessage('No suppliers to export for the current filter.', 'info');
        return;
      }

      const supplierCategoryMap = Array.isArray(supplierCategoryOptions)
        ? supplierCategoryOptions.reduce((acc, category) => {
            if (category?.supplier_category_id) {
              acc[category.supplier_category_id] = category.name || category.supplier_category_id;
            }
            return acc;
          }, {})
        : {};

      const headers = [
        'Supplier ID',
        'Name',
        'Supplier Category',
        'Nominal Code',
        'Tax Code',
        'Supplier Status',
        'Created On',
      ];

      const escapeCsv = (value) => {
        const stringValue = value === null || value === undefined ? '' : String(value);
        return `"${stringValue.replace(/"/g, '""')}"`;
      };

      const rows = filteredSuppliers.map((supplier) => {
        const supplierCategory =
          supplierCategoryMap[supplier.supplier_category_id] || supplier.supplier_category_id || '';
        const nominalCode =
          (supplier.nominal_code_id ? nominalCodeMap[supplier.nominal_code_id] : '') || supplier.nominal_code_id || '';
        const taxCode =
          (supplier.tax_code_id ? taxCodeMap[supplier.tax_code_id] : '') || supplier.tax_code_id || '';
        const supplierStatus =
          supplierStatusOptions.find((option) => option.supplier_status_id === supplier.supplier_status_id)?.name ||
          supplier.supplier_status_id ||
          '';
        return [
          supplier.supplier_id || '',
          supplier.name || '',
          supplierCategory,
          nominalCode,
          taxCode,
          supplierStatus,
          supplier.created_on ? formatDate(supplier.created_on) || supplier.created_on : '',
        ].map(escapeCsv).join(',');
      });

      const csvContent = [headers.map(escapeCsv).join(','), ...rows].join('\n');
      const blob = new Blob([csvContent], { type: 'text/csv;charset=utf-8;' });
      const url = URL.createObjectURL(blob);
      const link = document.createElement('a');
      link.href = url;
      const timestamp = new Date().toISOString().split('T')[0];
      link.download = `suppliers-${timestamp}.csv`;
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
      URL.revokeObjectURL(url);

      showMessage('Export complete. CSV downloaded.', 'success');
    } catch (error) {
      console.error('Error exporting suppliers CSV:', error);
      showMessage('Failed to export CSV. Please try again.', 'error');
    } finally {
      setExportingSuppliers(false);
    }
  };

  return (
    <Box className="suppliers-page">
        <Box className="filterRow" sx={{ mb: 0, flexWrap: 'wrap' }}>
        <Box className="filterElement filterElementThird" sx={{ minWidth: 260, py: 1.5, mb: 0, flex: 1, maxWidth: 'none' }}>
          <Typography variant="subtitle1" className="filterTitle">
            Filter by Supplier
          </Typography>
          <Autocomplete
            options={supplierFilterOptions}
            value={selectedSupplierFilter}
            onChange={handleSupplierFilterChange}
            getOptionLabel={(option) => (!option ? '' : `${option.supplier_id} - ${option.name || option.supplier_id}`)}
            isOptionEqualToValue={(option, value) => option?.supplier_id === value?.supplier_id}
            renderOption={(props, option) => (
              <li {...props} key={option.supplier_id}>
                <span>{option.supplier_id}</span>
                <span style={{ marginLeft: 8 }}>{option.name}</span>
              </li>
            )}
            clearOnEscape
            loading={loading && supplierFilterOptions.length === 0}
            disabled={loading && supplierFilterOptions.length === 0}
            renderInput={(params) => (
              <TextField
                {...params}
                placeholder={loading && supplierFilterOptions.length === 0 ? 'Loading suppliers…' : 'Select supplier'}
                fullWidth
              />
            )}
          />
        </Box>
        <Box className="filterElement filterElementThird" sx={{ minWidth: 220, py: 1.5, mb: 0, flex: 1, maxWidth: 'none' }}>
          <Typography variant="subtitle1" className="filterTitle">
            Nominal Code
          </Typography>
          <Autocomplete
            options={supplierNominalOptions}
            value={selectedNominalFilter}
            onChange={handleNominalFilterChange}
            getOptionLabel={(option) => (!option ? '' : `${option.nominal_code_id} - ${option.name || option.nominal_code_id}`)}
            isOptionEqualToValue={(option, value) => option?.nominal_code_id === value?.nominal_code_id}
            clearOnEscape
            renderInput={(params) => (
              <TextField
                {...params}
                placeholder="Select nominal code"
                fullWidth
              />
            )}
          />
        </Box>
      </Box>
      <Box className="filterRow" sx={{ mb: 0, flexWrap: 'wrap' }}>
        <Box className="filterElement filterElementThird" sx={{ minWidth: 220, py: 1.5, mb: 0, flex: 1, maxWidth: 'none' }}>
          <Typography variant="subtitle1" className="filterTitle">
            Supplier Category
          </Typography>
          <Autocomplete
            options={supplierCategoryOptions}
            value={selectedSupplierCategoryFilter}
            onChange={handleSupplierCategoryFilterChange}
            getOptionLabel={(option) => (!option ? '' : option.name || option.supplier_category_id)}
            isOptionEqualToValue={(option, value) => option?.supplier_category_id === value?.supplier_category_id}
            clearOnEscape
            renderInput={(params) => (
              <TextField
                {...params}
                placeholder="Select category"
                fullWidth
              />
            )}
          />
        </Box>
        <Box className="filterElement filterElementThird" sx={{ minWidth: 220, py: 1.5, mb: 0, flex: 1, maxWidth: 'none' }}>
          <Typography variant="subtitle1" className="filterTitle">
            Tax Code
          </Typography>
          <Autocomplete
            options={supplierTaxOptions}
            value={selectedTaxFilter}
            onChange={handleTaxFilterChange}
            getOptionLabel={(option) => (!option ? '' : `${option.tax_code_id} - ${option.name || option.tax_code_id}`)}
            isOptionEqualToValue={(option, value) => option?.tax_code_id === value?.tax_code_id}
            clearOnEscape
            renderInput={(params) => (
              <TextField
                {...params}
                placeholder="Select tax code"
                fullWidth
              />
            )}
          />
        </Box>
        <Box className="filterElement filterElementThird" sx={{ minWidth: 200, py: 1.5, mb: 0, flex: 1, maxWidth: 'none' }}>
          <Typography variant="subtitle1" className="filterTitle">
            Supplier Status
          </Typography>
          <Autocomplete
            options={supplierStatusOptions}
            value={selectedSupplierStatusFilter}
            onChange={handleSupplierStatusFilterChange}
            getOptionLabel={(option) => (!option ? '' : option.name || option.supplier_status_id)}
            isOptionEqualToValue={(option, value) => option?.supplier_status_id === value?.supplier_status_id}
            clearOnEscape
            renderInput={(params) => (
              <TextField
                {...params}
                placeholder="Select status"
                fullWidth
              />
            )}
          />
        </Box>
      </Box>
      <Box className="clearButton" sx={{ mt: 1, mb: 2, alignSelf: 'flex-end' }}>
        {hasActiveFilters && (
          <Button
            variant="outlined"
            startIcon={<Clear />}
            onClick={handleClearFilters}
            size="small"
            sx={{ minWidth: 0, px: 1.5 }}
          >
            Clear Filters
          </Button>
        )}
      </Box>

      <Box className="suppliers-page__header">
        <Typography variant="h5" component="h1">
          Suppliers
        </Typography>
        <Box className="suppliers-page__actions">
          <Button
            variant="contained"
            startIcon={<FileDownload />}
            onClick={handleExportSuppliersCsv}
            disabled={exportingSuppliers || loading}
          >
            {exportingSuppliers ? 'Exporting…' : 'Export CSV'}
          </Button>
          {!isReaderOnly && (
            <>
              <Button
                variant="outlined"
                startIcon={<CloudDownload />}
                onClick={handleImportSuppliers}
                disabled
              >
                Import Suppliers
              </Button>
              <Button
                variant="contained"
                startIcon={<Add />}
                onClick={openCreateDialog}
                disabled={busyAction}
              >
                Add Supplier
              </Button>
            </>
          )}
        </Box>
      </Box>

        <Paper elevation={1}>
          <TableContainer
            sx={{
              position: 'relative',
              minHeight: isTableLoading && !hasTableRows ? 200 : 'auto',
            }}
          >
            {isTableLoading && hasTableRows && (
              <Box
                sx={{
                  position: 'absolute',
                  inset: 0,
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  bgcolor: 'rgba(255, 255, 255, 0.65)',
                  pointerEvents: 'none',
                  zIndex: 2,
                }}
              >
                <CircularProgress size={24} thickness={4} />
              </Box>
            )}
            <Table className="suppliers-page__table">
              <TableHead>
                <TableRow>
                  <TableCell
                    className={`sortableHeader ${sortKey === 'supplier_id' ? 'sorted' : ''}`}
                    onClick={() => handleSortClick('supplier_id')}
                    onMouseEnter={() => setHoveredHeader('supplier_id')}
                    onMouseLeave={() => setHoveredHeader(null)}
                  >
                    <div className="headerContent">
                      <strong>Supplier ID</strong>
                      {(sortKey === 'supplier_id' || hoveredHeader === 'supplier_id') &&
                        (sortKey === 'supplier_id' ? (
                          sortDirection === 'asc' ? (
                            <ArrowUpward className="sortIcon" />
                          ) : (
                            <ArrowDownward className="sortIcon" />
                          )
                        ) : (
                          <ArrowDownward className="hoverIcon" />
                        ))}
                    </div>
                  </TableCell>
                  <TableCell
                    className={`sortableHeader ${sortKey === 'name' ? 'sorted' : ''}`}
                    onClick={() => handleSortClick('name')}
                    onMouseEnter={() => setHoveredHeader('name')}
                    onMouseLeave={() => setHoveredHeader(null)}
                  >
                    <div className="headerContent">
                      <strong>Name</strong>
                      {(sortKey === 'name' || hoveredHeader === 'name') &&
                        (sortKey === 'name' ? (
                          sortDirection === 'asc' ? (
                            <ArrowUpward className="sortIcon" />
                          ) : (
                            <ArrowDownward className="sortIcon" />
                          )
                        ) : (
                          <ArrowDownward className="hoverIcon" />
                        ))}
                    </div>
                  </TableCell>
                  <TableCell
                    className={`sortableHeader ${sortKey === 'supplier_category_id' ? 'sorted' : ''}`}
                    onClick={() => handleSortClick('supplier_category_id')}
                    onMouseEnter={() => setHoveredHeader('supplier_category_id')}
                    onMouseLeave={() => setHoveredHeader(null)}
                  >
                    <div className="headerContent">
                      <strong>Supplier Category</strong>
                      {(sortKey === 'supplier_category_id' || hoveredHeader === 'supplier_category_id') &&
                        (sortKey === 'supplier_category_id' ? (
                          sortDirection === 'asc' ? (
                            <ArrowUpward className="sortIcon" />
                          ) : (
                            <ArrowDownward className="sortIcon" />
                          )
                        ) : (
                          <ArrowDownward className="hoverIcon" />
                        ))}
                    </div>
                  </TableCell>
                  <TableCell
                    className={`sortableHeader ${sortKey === 'nominal_code_id' ? 'sorted' : ''}`}
                    onClick={() => handleSortClick('nominal_code_id')}
                    onMouseEnter={() => setHoveredHeader('nominal_code_id')}
                    onMouseLeave={() => setHoveredHeader(null)}
                  >
                    <div className="headerContent">
                      <strong>Nominal Code</strong>
                      {(sortKey === 'nominal_code_id' || hoveredHeader === 'nominal_code_id') &&
                        (sortKey === 'nominal_code_id' ? (
                          sortDirection === 'asc' ? (
                            <ArrowUpward className="sortIcon" />
                          ) : (
                            <ArrowDownward className="sortIcon" />
                          )
                        ) : (
                          <ArrowDownward className="hoverIcon" />
                        ))}
                    </div>
                  </TableCell>
                  <TableCell
                    className={`sortableHeader ${sortKey === 'tax_code_id' ? 'sorted' : ''}`}
                    onClick={() => handleSortClick('tax_code_id')}
                    onMouseEnter={() => setHoveredHeader('tax_code_id')}
                    onMouseLeave={() => setHoveredHeader(null)}
                  >
                    <div className="headerContent">
                      <strong>Tax Code</strong>
                      {(sortKey === 'tax_code_id' || hoveredHeader === 'tax_code_id') &&
                        (sortKey === 'tax_code_id' ? (
                          sortDirection === 'asc' ? (
                            <ArrowUpward className="sortIcon" />
                          ) : (
                            <ArrowDownward className="sortIcon" />
                          )
                        ) : (
                          <ArrowDownward className="hoverIcon" />
                        ))}
                    </div>
                  </TableCell>
                  <TableCell
                    className={`sortableHeader ${sortKey === 'supplier_status_id' ? 'sorted' : ''}`}
                    onClick={() => handleSortClick('supplier_status_id')}
                    onMouseEnter={() => setHoveredHeader('supplier_status_id')}
                    onMouseLeave={() => setHoveredHeader(null)}
                  >
                    <div className="headerContent">
                      <strong>Supplier Status</strong>
                      {(sortKey === 'supplier_status_id' || hoveredHeader === 'supplier_status_id') &&
                        (sortKey === 'supplier_status_id' ? (
                          sortDirection === 'asc' ? (
                            <ArrowUpward className="sortIcon" />
                          ) : (
                            <ArrowDownward className="sortIcon" />
                          )
                        ) : (
                          <ArrowDownward className="hoverIcon" />
                        ))}
                    </div>
                  </TableCell>
                  <TableCell
                    className={`sortableHeader ${sortKey === 'created_on' ? 'sorted' : ''}`}
                    onClick={() => handleSortClick('created_on')}
                    onMouseEnter={() => setHoveredHeader('created_on')}
                    onMouseLeave={() => setHoveredHeader(null)}
                  >
                    <div className="headerContent">
                      <strong>Created On</strong>
                      {(sortKey === 'created_on' || hoveredHeader === 'created_on') &&
                        (sortKey === 'created_on' ? (
                          sortDirection === 'asc' ? (
                            <ArrowUpward className="sortIcon" />
                          ) : (
                            <ArrowDownward className="sortIcon" />
                          )
                        ) : (
                          <ArrowDownward className="hoverIcon" />
                        ))}
                    </div>
                  </TableCell>
                  <TableCell
                    className={`sortableHeader ${sortKey === 'discount' ? 'sorted' : ''}`}
                    onClick={() => handleSortClick('discount')}
                    onMouseEnter={() => setHoveredHeader('discount')}
                    onMouseLeave={() => setHoveredHeader(null)}
                  >
                    <div className="headerContent">
                      <strong>Discount</strong>
                      {(sortKey === 'discount' || hoveredHeader === 'discount') &&
                        (sortKey === 'discount' ? (
                          sortDirection === 'asc' ? (
                            <ArrowUpward className="sortIcon" />
                          ) : (
                            <ArrowDownward className="sortIcon" />
                          )
                        ) : (
                          <ArrowDownward className="hoverIcon" />
                        ))}
                    </div>
                  </TableCell>
                  {!isReaderOnly && (
                    <TableCell align="right"><strong>Actions</strong></TableCell>
                  )}
                </TableRow>
              </TableHead>
              <TableBody>
                {hasTableRows ? (
                  paginatedSuppliers.map((supplier) => (
                    <TableRow key={supplier.supplier_id} hover>
                      <TableCell>{supplier.supplier_id}</TableCell>
                      <TableCell>{supplier.name}</TableCell>
                      <TableCell>
                        {supplier.supplier_category_id
                          ? supplierCategoryOptions.find(
                              (option) => option.supplier_category_id === supplier.supplier_category_id
                            )?.name || supplier.supplier_category_id
                          : '-'}
                      </TableCell>
                      <TableCell>
                        {supplier.nominal_code_id
                          ? nominalCodeMap[supplier.nominal_code_id] || supplier.nominal_code_id
                          : '-'}
                      </TableCell>
                      <TableCell>
                        {supplier.tax_code_id
                          ? taxCodeMap[supplier.tax_code_id] || supplier.tax_code_id
                          : '-'}
                      </TableCell>
                      <TableCell>
                        {supplier.supplier_status_id
                          ? supplierStatusOptions.find(
                              (option) => option.supplier_status_id === supplier.supplier_status_id
                            )?.name || supplier.supplier_status_id
                          : '-'}
                      </TableCell>
                      <TableCell>
                        {supplier.created_on ? formatDate(supplier.created_on) || '-' : '-'}
                      </TableCell>
                      <TableCell>
                        {formatDiscountDisplay(supplier.discount)}
                      </TableCell>
                      {!isReaderOnly && (
                        <TableCell align="right">
                          <IconButton
                            aria-label="edit supplier"
                            onClick={() => openEditDialog(supplier)}
                            size="small"
                          >
                            <Edit fontSize="small" />
                          </IconButton>
                          <IconButton
                            aria-label="delete supplier"
                            onClick={() => confirmDeleteSupplier(supplier)}
                            size="small"
                            color="error"
                            sx={{ display: isAdmin ? 'inline-flex' : 'none' }}
                          >
                            <Delete sx={{ fontSize: 18 }} />
                          </IconButton>
                        </TableCell>
                      )}
                    </TableRow>
                  ))
                ) : isTableLoading ? (
                  <TableRow>
                    <TableCell colSpan={isReaderOnly ? 6 : 7} align="center" sx={{ py: 3 }}>
                      <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 1.5 }}>
                        <CircularProgress size={18} thickness={4} />
                        <Typography color="text.secondary">Loading...</Typography>
                      </Box>
                    </TableCell>
                  </TableRow>
                ) : (
                  <TableRow>
                    <TableCell colSpan={isReaderOnly ? 6 : 7} align="center">
                      <Typography color="text.secondary">No record(s) found.</Typography>
                    </TableCell>
                  </TableRow>
                )}
              </TableBody>
              <TableFooter>
                  <TableRow>
                    <TableCell colSpan={isReaderOnly ? 8 : 9}>
          <Box
            sx={{
              display: 'flex',
              justifyContent: 'flex-end',
              alignItems: 'center',
              flexWrap: 'wrap',
              gap: 2,
              padding: '0px 16px',
              mt: 0
            }}
          >
            
              
              <TextField
                value={pageInput}
                onChange={handlePageInputChange}
                onKeyDown={handlePageInputKeyDown}
                label="Page"
                size="small"
                sx={{ width: 90 }}
                inputProps={{ inputMode: 'numeric', pattern: '[0-9]*' }}
                disabled={loading || pageChanging || !filteredSuppliers.length}
              />
              <Typography variant="body2">
                {filteredSuppliers.length ? `of ${totalPages}` : ''}
              </Typography>
            <Typography variant="body2">{footerSummary}</Typography>
            <TablePagination
              component="div"
              count={filteredSuppliers.length}
              page={page}
              onPageChange={handlePageChange}
              rowsPerPage={rowsPerPage}
              rowsPerPageOptions={[rowsPerPage]}
              labelRowsPerPage=""
              labelDisplayedRows={() => ''}
              ActionsComponent={(paginationProps) => (
                <PaginationActions
                  page={paginationProps.page}
                  count={paginationProps.count}
                  rowsPerPage={paginationProps.rowsPerPage}
                  onPageChange={paginationProps.onPageChange}
                  disabled={isTableLoading || !filteredSuppliers.length}
                />
              )}
              sx={{ '.MuiTablePagination-toolbar': { p: 0 }, '.MuiTablePagination-selectLabel': { display: 'none' }, '.MuiTablePagination-displayedRows': { m: 0 } }}
            />
          </Box>
          </TableCell>
          </TableRow>
          </TableFooter>
            </Table>
          </TableContainer>
          
        </Paper>




      <Dialog open={dialogOpen} onClose={closeDialog} fullWidth maxWidth="sm">
        <DialogTitle>{dialogMode === 'create' ? 'Add Supplier' : 'Edit Supplier'}</DialogTitle>
        <DialogContent dividers>
          <Box className="suppliers-page__dialog-content">
            <TextField
              label="Supplier ID *"
              value={formData.supplier_id}
              onChange={(event) => handleFormChange('supplier_id', event.target.value)}
              fullWidth
              margin="normal"
              error={Boolean(formErrors.supplier_id)}
              helperText={formErrors.supplier_id}
              disabled={dialogMode === 'edit'}
            />
            <TextField
              label="Name *"
              value={formData.name}
              onChange={(event) => handleFormChange('name', event.target.value)}
              fullWidth
              margin="normal"
              error={Boolean(formErrors.name)}
              helperText={formErrors.name}
            />
            <Autocomplete
              options={supplierNominalOptions}
              value={supplierNominalOptions.find((option) => option.nominal_code_id === formData.nominal_code_id) || null}
              getOptionLabel={(option) => (!option ? '' : `${option.nominal_code_id} - ${option.name || option.nominal_code_id}`)}
              isOptionEqualToValue={(option, value) => option.nominal_code_id === value?.nominal_code_id}
              onChange={(_, option) => handleFormChange('nominal_code_id', option ? option.nominal_code_id : '')}
              renderInput={(params) => (
                <TextField
                  {...params}
                  label="Nominal Code"
                  margin="normal"
                  fullWidth
                />
              )}
            />
            <Autocomplete
              options={supplierTaxOptions}
              value={supplierTaxOptions.find((option) => option.tax_code_id === formData.tax_code_id) || null}
              getOptionLabel={(option) => (!option ? '' : `${option.tax_code_id} - ${option.name || option.tax_code_id}`)}
              isOptionEqualToValue={(option, value) => option.tax_code_id === value?.tax_code_id}
              onChange={(_, option) => handleFormChange('tax_code_id', option ? option.tax_code_id : '')}
              renderInput={(params) => (
                <TextField
                  {...params}
                  label="Tax Code"
                  margin="normal"
                  fullWidth
                />
              )}
            />
            <Autocomplete
              options={supplierCategoryOptions}
              value={
                supplierCategoryOptions.find(
                  (option) => option.supplier_category_id === formData.supplier_category_id
                ) || null
              }
              getOptionLabel={(option) => (!option ? '' : option.name || option.supplier_category_id)}
              isOptionEqualToValue={(option, value) => option.supplier_category_id === value?.supplier_category_id}
              onChange={(_, option) => handleFormChange('supplier_category_id', option ? option.supplier_category_id : '')}
              renderInput={(params) => (
                <TextField
                  {...params}
                  label="Supplier Category"
                  margin="normal"
                  fullWidth
                />
              )}
            />
            <Autocomplete
              options={supplierStatusOptions}
              value={
                supplierStatusOptions.find(
                  (option) => option.supplier_status_id === formData.supplier_status_id
                ) || null
              }
              getOptionLabel={(option) => (!option ? '' : option.name || option.supplier_status_id)}
              isOptionEqualToValue={(option, value) => option.supplier_status_id === value?.supplier_status_id}
              onChange={(_, option) => handleFormChange('supplier_status_id', option ? option.supplier_status_id : '')}
              renderInput={(params) => (
                <TextField
                  {...params}
                  label="Supplier Status"
                  margin="normal"
                  fullWidth
                />
              )}
            />
            <TextField
              label="Discount Start Date"
              type="date"
              value={formData.discount_start_date || ''}
              onChange={(event) => handleFormChange('discount_start_date', event.target.value)}
              InputLabelProps={{ shrink: true }}
              fullWidth
              margin="normal"
            />
            <TextField
              label="Discount"
              type="number"
              value={formData.discount}
              onChange={(event) => {
                handleFormChange('discount', event.target.value);
              }}
              onBlur={() => {
                if (formData.discount === '' || formData.discount === null || formData.discount === undefined) {
                  handleFormChange('discount', '0');
                }
              }}
              fullWidth
              margin="normal"
              inputProps={{ step: '0.01', min: '0' }}
              InputProps={{
                endAdornment: <InputAdornment position="end">%</InputAdornment>,
              }}
            />
            <FormControlLabel
              control={
                <Switch
                  checked={Boolean(formData.active)}
                  onChange={(event) => handleFormChange('active', event.target.checked)}
                  color="primary"
                />
              }
              label="Active"
            />
          </Box>
        </DialogContent>
        <DialogActions>
          <Button onClick={closeDialog} disabled={busyAction}>
            Cancel
          </Button>
          <Button onClick={handleSaveSupplier} variant="contained" disabled={busyAction}>
            {dialogMode === 'create' ? 'Create' : 'Save'}
          </Button>
        </DialogActions>
      </Dialog>

      <Dialog open={Boolean(deleteTarget)} onClose={cancelDelete} maxWidth="xs" fullWidth>
        <DialogTitle>Delete Supplier</DialogTitle>
        <DialogContent dividers>
          <Typography>
            Are you sure you want to delete supplier "{deleteTarget?.name}" ({deleteTarget?.supplier_id})?
          </Typography>
        </DialogContent>
        <DialogActions>
          <Button onClick={cancelDelete} disabled={busyAction}>
            Cancel
          </Button>
          <Button
            onClick={handleDeleteSupplier}
            variant="contained"
            color="error"
            disabled={busyAction || !isAdmin}
          >
            Delete
          </Button>
        </DialogActions>
      </Dialog>

      <Snackbar
        open={snackbar.open}
        autoHideDuration={6000}
        onClose={closeSnackbar}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'center' }}
      >
        <Alert onClose={closeSnackbar} severity={snackbar.severity} sx={{ width: '100%' }}>
          {snackbar.message}
        </Alert>
      </Snackbar>
    </Box>
  );
};

export default SuppliersPage;
